import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import useIsMountedRef from 'use-is-mounted-ref'
import { ButtonBase } from '@material-ui/core'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../Common/Contexts/LanguageContext'
import { TeamsAuth as TeamsAuthLanguage } from '../../../../i18n'
import { MessageContext, ReactState } from '../../Common/Contexts'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { SecurityGroups, TelSysAdminDTO } from '../../../NonReact/Types/Types'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { OAuthButton } from '../../Common/Components/OAuthButton/OAuthButton'
import { OAuthConfig } from '../../Common/Components/OAuthButton/Types/Types'
import { getOAuthConfig } from '../../Common/API/ConfigAPI'
import { ALL_VALUE, PRIMARY_PARTITION } from '../../Common/API/ItemsGetters'
import { useBranding } from '../../Common/Hooks/useBranding'
import { Branding } from '../../Common/API/BrandingAPI'
import Button from '../../Common/Components/Button'
import Icon, { IconName } from '../../Common/Components/Icon'
import Loader from '../../Common/Components/Loader'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { getSecurityGroupsURL, telSysUpdateURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { signOut } from '../../Common/API/UserSignInOutAPI'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { DarkModeType, useDarkMode } from '../../Common/Hooks/useDarkMode'
import { configureFullURLForLocalDevelopment } from '../../Common/Utils/LocalUtils'
import { AUTH_ROUTE_PATH } from '../../../ReactRouting/Constants/Constants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { getTelSys } from '../../../NonReact/common/API/EntityAPIClient'
import { WelcomeContainer } from '../../../ReactRouting/Components/Organisms/WelcomeContainer/WelcomeContainer'
import { SyncEnvironments } from '../../TelSysConfiguration/Constants/Constants'
import { SecurityGroup, SecurityGroupFormEntry } from '../Types/Types'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { updatePartitionFromPartial } from '../../Common/API/PartitionAPI'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { ReactStateRef, useStateAsRef } from '../../Common/Hooks/useStateAsRef'
import { catchAndTryAgainPromise } from '../../../NonReact/common/API/REST/RESTSyncRetry'

type Props = {
    isRouterMode?: boolean
}

enum Pages {
    Auth,
    AuthoriserSelect,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    SecurityGroups
}

export const TeamsAuth: FC<Props> = ({ isRouterMode }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(TeamsAuthLanguage.NAME)
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [userInfo]: UserInfo = useUserInfo()
    const branding: Nullable<Branding> = useBranding()

    let navigate: Nullable<NavigateFunction> = null
    let location: Nullable<Location> = null

    if (isRouterMode) {
        navigate = useNavigate()
        location = useLocation()
    }

    const { isDarkMode }: DarkModeType = useDarkMode()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [error, setError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isAuthenticated, setIsAuthenticated]: ReactState<boolean> = useState<boolean>(false)
    const [isAuthenticating, setIsAuthenticating]: ReactState<boolean> = useState<boolean>(false)
    const [config, setConfig]: ReactState<Nullable<OAuthConfig>> = useState<Nullable<OAuthConfig>>()
    const [telSys, setTelSys]: ReactState<Nullable<TelSysAdminDTO>> = useState<Nullable<TelSysAdminDTO>>()
    const [statusesToDisplay, setStatusesToDisplay]: ReactState<Nullable<ReactElement[]>> =
        useState<Nullable<ReactElement[]>>()
    const [syncEnvironmentMode, setSyncEnvironmentMode]: ReactStateRef<SyncEnvironments> =
        useStateAsRef<SyncEnvironments>(SyncEnvironments.TENANT)
    const [currentPage, setCurrentPage]: ReactState<Pages> = useState<Pages>(Pages.Auth)
    const [securityGroups, setSecurityGroups]: ReactState<Nullable<SecurityGroupFormEntry[]>> =
        useState<Nullable<SecurityGroupFormEntry[]>>()
    const [isReAuth, setIsReAuth]: ReactState<boolean> = useState<boolean>(false)

    const userTelsysID: Definable<number> = useMemo((): Definable<number> => userInfo?.Scope?.TelSysID?.[0], [userInfo])
    const userPartitionID: Definable<number> = useMemo(
        (): number =>
            (userInfo?.Scope?.PartIDInTelSys?.[0] === ALL_VALUE
                ? PRIMARY_PARTITION
                : userInfo?.Scope?.PartIDInTelSys?.[0]) ?? PRIMARY_PARTITION,
        [userInfo]
    )

    const checkedSecurityGroups: SecurityGroup[] = useMemo(
        (): SecurityGroup[] =>
            securityGroups
                ?.filter(({ checked }: SecurityGroupFormEntry): boolean => checked)
                .map(({ group }: SecurityGroupFormEntry): SecurityGroup => group) ?? [],
        [securityGroups]
    )

    const mainLogoURL: string = useMemo((): string => {
        if (isRouterMode) {
            return (
                (isDarkMode
                    ? branding?.BrandingConfig?.imagery?.mainLogo
                    : branding?.BrandingConfig?.imagery?.mainLogoForWhiteBG) ?? EMPTY_STRING
            )
        }

        return branding?.LogoURL ?? EMPTY_STRING
    }, [isRouterMode, isDarkMode, branding])

    const completeAndLogout = (): void => {
        if (isMounted.current) {
            setIsAuthenticating(false)
            setIsAuthenticated(true)

            setTimeout(
                (): void =>
                    signOut(
                        isRouterMode
                            ? (): void =>
                                  navigate?.(AUTH_ROUTE_PATH, {
                                      replace: true,
                                      state: { location }
                                  })
                            : undefined
                    ),
                1000
            )
        }
    }

    const onAuthenticating = (): void => {
        setError(undefined)
        setIsAuthenticated(false)
        setIsAuthenticating(true)
    }

    const onOAuthFailure = (e?: Error): void => {
        setError(e?.message ?? language.get(TeamsAuthLanguage.TeamsAuth_Error_Unknown))
        setIsAuthenticating(false)
    }

    const onOAuthSuccess = useCallback(
        (sessionID?: number): void => {
            if (telSys?.TelSysID) {
                requestJSON<void, TelSysAdminDTO>(telSysUpdateURL(telSys?.TelSysID), {
                    method: RESTMethods.PUT,
                    body: {
                        ...telSys,
                        ...(IsVarEmpty(telSys?.Advanced)
                            ? {}
                            : {
                                  Advanced: {
                                      ...telSys.Advanced,
                                      MaximumAllowedUsersMonitored: undefined,
                                      MaximumAllowedUsersSync: undefined
                                  }
                              }),
                        AuthPassword2OAuthSessionID: sessionID?.toString()
                    }
                })
                    .then((): void => {
                        if (isMounted.current) {
                            if (syncEnvironmentMode.current === SyncEnvironments.TENANT || isReAuth) {
                                completeAndLogout()
                            } else if (syncEnvironmentMode.current === SyncEnvironments.AUTHORISER_SELECT) {
                                setCurrentPage(Pages.AuthoriserSelect)
                            } else if (syncEnvironmentMode.current === SyncEnvironments.SECURITY_GROUPS) {
                                setCurrentPage(Pages.SecurityGroups)
                            }
                        }
                    })
                    .catch(onOAuthFailure)
            } else {
                onOAuthFailure()
            }
        },
        [telSys, syncEnvironmentMode.current, isReAuth]
    )

    const authoriserSelectsSecurityGroups = useCallback((): void => {
        if (
            !ArrayUtils.isEmpty(checkedSecurityGroups) &&
            isVarNotEmpty(userTelsysID) &&
            isVarNotEmpty(userPartitionID)
        ) {
            updatePartitionFromPartial(userTelsysID, userPartitionID, {
                SyncConfigurations: {
                    SyncEnvironment: SyncEnvironments.SECURITY_GROUPS,
                    SecurityGroups: checkedSecurityGroups.reduce(
                        (accum: SecurityGroups, next: SecurityGroup): SecurityGroups => ({
                            GroupIDs: [...accum.GroupIDs, next.id],
                            GroupNames: [...accum.GroupNames, next.displayName]
                        }),
                        {
                            GroupIDs: [],
                            GroupNames: []
                        } as SecurityGroups
                    )
                },
                SyncNow: true
            })
                .then(completeAndLogout)
                .catch(displayError)
        }
    }, [checkedSecurityGroups, userTelsysID, userPartitionID])

    const authoriserSelectsTenant = useCallback((): void => {
        if (isVarNotEmpty(userTelsysID) && isVarNotEmpty(userPartitionID)) {
            updatePartitionFromPartial(userTelsysID, userPartitionID, {
                SyncConfigurations: {
                    SyncEnvironment: SyncEnvironments.TENANT
                },
                SyncNow: true
            })
                .then(completeAndLogout)
                .catch(displayError)
        }
    }, [userTelsysID, userPartitionID])

    useEffect((): void => {
        if (userInfo) {
            if (
                (userInfo?.Scope?.TelSysID?.length ?? 0) !== 1 ||
                userInfo?.Scope?.TelSysID[0] === ALL_VALUE ||
                !PermissionsUtil.hasPermissions(
                    userInfo,
                    PermissionsUtil.builder(AdminBackendPermissionNames.TEL_SYS).update().read()
                )
            ) {
                return signOut(
                    isRouterMode
                        ? (): void =>
                              navigate?.(AUTH_ROUTE_PATH, {
                                  replace: true,
                                  state: { location }
                              })
                        : undefined
                )
            }

            getTelSys(userInfo?.Scope?.TelSysID[0])
                .then((telephonyServer: Nullable<TelSysAdminDTO>): void => {
                    if (isMounted.current) {
                        if (telephonyServer?.CommsEnabled && telephonyServer?.TelSysType === TelSysTypes.MS_TEAMS) {
                            setTelSys(telephonyServer)

                            if (telephonyServer?.MonitoringEnabled) {
                                setIsReAuth(true)
                            }

                            setSyncEnvironmentMode(
                                telephonyServer?.SyncEnvironmentType ?? SyncEnvironments.AUTHORISER_SELECT
                            )
                        } else {
                            signOut(
                                isRouterMode
                                    ? (): void =>
                                          navigate?.(AUTH_ROUTE_PATH, {
                                              replace: true,
                                              state: { location }
                                          })
                                    : undefined
                            )
                        }
                    }
                })
                .catch((e: Error): void => {
                    displayError(e)
                    setTimeout(signOut, 2000)
                })

            getOAuthConfig(TelSysTypes.MS_TEAMS)
                .then((oAuthConfig: Nullable<OAuthConfig>): void => {
                    if (isMounted.current) {
                        setConfig(oAuthConfig)
                    }
                })
                .catch(displayError)
        }

        return undefined
    }, [userInfo, isRouterMode])

    useEffect((): void => {
        if (currentPage === Pages.SecurityGroups && isVarNotEmpty(userInfo) && isVarNotEmpty(userTelsysID)) {
            setSecurityGroups(null)

            setTimeout((): void => {
                catchAndTryAgainPromise<SecurityGroup[]>(
                    (): Promise<JSONResponse<SecurityGroup[]>> =>
                        requestGetJSON<SecurityGroup[]>(getSecurityGroupsURL(userTelsysID))
                )
                    .then((secGroups: JSONResponse<SecurityGroup[]>): void => {
                        if (isMounted.current) {
                            setSecurityGroups(
                                secGroups?.map(
                                    (secGroup: SecurityGroup): SecurityGroupFormEntry => ({
                                        checked: false,
                                        group: secGroup
                                    })
                                ) ?? []
                            )
                        }
                    })
                    .catch((e: Error): void => {
                        if (isMounted.current) {
                            displayError(e)
                            setSecurityGroups([])
                        }
                    })
            })
        }
    }, [userInfo, currentPage])

    return (
        <WelcomeContainer>
            <div
                className={`${isRouterMode ? 'router-teams-auth' : 'teams-auth-content'} ${
                    isAuthenticating ? 'is-authenticating' : ''
                } ${isAuthenticated ? 'is-authenticated' : ''} ${isStringNotEmpty(error) ? 'is-error' : ''}`}
            >
                <div className='ta-logo-container'>
                    <img
                        className='ta-logo'
                        src={configureFullURLForLocalDevelopment(mainLogoURL)}
                        alt='Company Logo'
                    />

                    {isAuthenticating ? <Loader className='authenticating-loader' /> : <></>}

                    <div className='link-icon-container'>
                        <Icon iconName={isAuthenticated ? IconName.LINKED : IconName.NOT_LINKED} />
                    </div>

                    <img className='ta-logo' src='/CCS/Styles/Images/Teams/teams-logo.png' alt='Teams Logo' />
                </div>

                {currentPage === Pages.AuthoriserSelect ? (
                    <div className='ta-authoriser-select'>
                        <h3 className='tas-title'>
                            {language.get(TeamsAuthLanguage.TeamsAuth_Authoriser_Select_Title)}
                        </h3>

                        <div className='tas-options'>
                            <ButtonBase className='tas-option' onClick={authoriserSelectsTenant}>
                                <Icon className='taso-icon' iconName={IconName.TENANT} />
                                <b className='taso-text'>
                                    {language.get(TeamsAuthLanguage.TeamsAuth_Authoriser_Select_Options_Tenant)}
                                </b>
                            </ButtonBase>
                            <ButtonBase
                                className='tas-option'
                                onClick={(): void => {
                                    setCurrentPage(Pages.SecurityGroups)
                                }}
                            >
                                <Icon className='taso-icon' iconName={IconName.SECURITY_GROUPS} />
                                <b className='taso-text'>
                                    {language.get(TeamsAuthLanguage.TeamsAuth_Authoriser_Select_Options_SecurityGroups)}
                                </b>
                            </ButtonBase>
                        </div>
                    </div>
                ) : (
                    <></>
                )}

                {currentPage === Pages.SecurityGroups ? (
                    <div className='ta-security-groups'>
                        {IsVarEmpty(securityGroups) ? (
                            <div>
                                <Loader className='security-groups-loader' />
                                <div className='sgl-title-container'>
                                    <b className='sgl-title'>
                                        {language.get(TeamsAuthLanguage.TeamsAuth_SecurityGroups_Loader_Title_1)}
                                    </b>
                                    <b className='sgl-title'>
                                        {language.get(TeamsAuthLanguage.TeamsAuth_SecurityGroups_Loader_Title_2)}
                                    </b>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <h3 className='tasg-title'>
                                    {language.get(TeamsAuthLanguage.TeamsAuth_SecurityGroups_Title)}
                                </h3>

                                <div className='tasg-container'>
                                    {ArrayUtils.isEmpty(securityGroups) ? (
                                        <b className='tasg-nothing-to-see'>
                                            {language.get(
                                                TeamsAuthLanguage.TeamsAuth_SecurityGroups_SecurityGroup_NothingToSee
                                            )}
                                        </b>
                                    ) : (
                                        <ul className='tasg-list'>
                                            {securityGroups?.map(
                                                (
                                                    {
                                                        checked,
                                                        group: { id, description, displayName }
                                                    }: SecurityGroupFormEntry,
                                                    index: number
                                                ): ReactElement => (
                                                    <li
                                                        className='tasg-security-group'
                                                        key={`security-groups-list-${index}`}
                                                        title={description}
                                                    >
                                                        <RouterCheckbox
                                                            labelRight={
                                                                <>
                                                                    {displayName}
                                                                    <span className='tasgsg-id'>
                                                                        {language.getInterpolated(
                                                                            TeamsAuthLanguage.TeamsAuth_SecurityGroups_SecurityGroup,
                                                                            id
                                                                        )}
                                                                    </span>
                                                                </>
                                                            }
                                                            isChecked={checked}
                                                            onChange={(): void => {
                                                                const newSecurityGroups: SecurityGroupFormEntry[] = [
                                                                    ...securityGroups
                                                                ]

                                                                newSecurityGroups[index].checked = !checked

                                                                setSecurityGroups(newSecurityGroups)
                                                            }}
                                                        />
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    )}
                                    <div className='tasg-actions'>
                                        <span className='tasga-note'>
                                            {language.get(TeamsAuthLanguage.TeamsAuth_SecurityGroups_Note)}
                                        </span>
                                        <RoundedButton
                                            className='tasga-sync'
                                            onClick={
                                                ArrayUtils.isEmpty(securityGroups)
                                                    ? authoriserSelectsTenant
                                                    : authoriserSelectsSecurityGroups
                                            }
                                            disabled={
                                                ArrayUtils.isEmpty(checkedSecurityGroups) &&
                                                !ArrayUtils.isEmpty(securityGroups)
                                            }
                                        >
                                            {ArrayUtils.isEmpty(securityGroups)
                                                ? language.get(
                                                      TeamsAuthLanguage.TeamsAuth_SecurityGroups_NoSecurityGroups
                                                  )
                                                : language.get(TeamsAuthLanguage.TeamsAuth_SecurityGroups_SyncAndClose)}
                                        </RoundedButton>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <></>
                )}

                {currentPage === Pages.Auth ? (
                    <>
                        <div className='ta-intro ta-intro-1'>
                            {LanguageUtils.formatLanguageEntry(
                                language.getInterpolated(
                                    TeamsAuthLanguage.TeamsAuth_UserIntro,
                                    isStringNotEmpty(userInfo?.FullName) ? userInfo?.FullName : userInfo?.Username
                                )
                            )}
                        </div>

                        <div className='ta-intro'>{language.get(TeamsAuthLanguage.TeamsAuth_UserIntro_2)}</div>

                        {isStringNotEmpty(error) && IsVarEmpty(statusesToDisplay) ? (
                            <div className='ta-error'>{error}</div>
                        ) : (
                            <></>
                        )}

                        {isVarNotEmpty(statusesToDisplay) && statusesToDisplay.length > 0 ? (
                            <div className='ta-statuses'>
                                {statusesToDisplay.map(
                                    (status: ReactElement, index: number): ReactElement => (
                                        <div key={`status-${index}`} className='ta-status'>
                                            {status}
                                        </div>
                                    )
                                )}
                            </div>
                        ) : (
                            <></>
                        )}

                        {isVarNotEmpty(config) && isVarNotEmpty(telSys) ? (
                            <div className={`ta-action-container`}>
                                {isRouterMode ? (
                                    <RoundedButton
                                        className='ta-decline'
                                        onClick={(): void =>
                                            signOut(
                                                isRouterMode
                                                    ? (): void =>
                                                          navigate?.(AUTH_ROUTE_PATH, {
                                                              replace: true,
                                                              state: { location }
                                                          })
                                                    : undefined
                                            )
                                        }
                                        isOutlined
                                        disabled={isAuthenticating || isAuthenticated}
                                    >
                                        {language.get(TeamsAuthLanguage.TeamsAuth_Decline)}
                                    </RoundedButton>
                                ) : (
                                    <Button
                                        customClass='ta-decline'
                                        label={language.get(TeamsAuthLanguage.TeamsAuth_Decline)}
                                        onClick={(): void => signOut()}
                                        isOutlined
                                        isDisabled={isAuthenticating || isAuthenticated}
                                    />
                                )}
                                <OAuthButton
                                    selectedTelSys={telSys?.TelSysID}
                                    integrationType={TelSysTypes.MS_TEAMS}
                                    config={config}
                                    onAuthenticating={onAuthenticating}
                                    onOAuthDisplayStatus={
                                        isRouterMode
                                            ? (statuses: ReactElement[]): void => setStatusesToDisplay(statuses)
                                            : undefined
                                    }
                                    onOAuthSuccess={onOAuthSuccess}
                                    onOAuthFailure={onOAuthFailure}
                                    oAuthMessageOptions={{
                                        preAuthorisationButtonTitle: language.get(
                                            TeamsAuthLanguage.TeamsAuth_AdminConsent
                                        ),
                                        preAuthorisedMessage: language.get(
                                            TeamsAuthLanguage.TeamsAuth_ConsentedMessage
                                        ),
                                        authorisationButtonTitle: language.get(TeamsAuthLanguage.TeamsAuth_Authorise),
                                        authorisedMessage: language.get(TeamsAuthLanguage.TeamsAuth_AuthorisedMessage)
                                    }}
                                    isRouterMode={isRouterMode}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </>
                ) : (
                    <></>
                )}
            </div>
        </WelcomeContainer>
    )
}
